import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip as RTooltip,
} from 'recharts';
import {
  Loader2, Inbox, Layers, CheckCircle2, XCircle, FileSignature, TrendingUp,
  Factory, Send, Reply, Sparkles, Flame, Handshake, Clock, Users, Activity,
} from 'lucide-react';
import { CollapsibleSection } from '../components/CollapsibleSection';

const MONTHS = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
const monthLabel = (k) => { const [y, m] = k.split('-'); return `${MONTHS[+m - 1]} ${y.slice(2)}`; };
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—';

const Kpi = ({ icon: Icon, label, value, sub, color = 'text-foreground', testId }) => (
  <div className="bg-card border border-border rounded-sm p-4" data-testid={testId}>
    <div className="flex items-center gap-2 text-muted-foreground">
      <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
      <span className="text-[10px] uppercase tracking-wider">{label}</span>
    </div>
    <div className={`text-2xl font-heading mt-2 ${color}`}>{value}</div>
    {sub && <div className="text-[10px] font-mono text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);

function BarRow({ label, count, max, right, active, passed, testId }) {
  const w = max > 0 ? Math.max(3, Math.round((count / max) * 100)) : 0;
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-foreground/85">{label}</span>
        <span className="font-mono text-muted-foreground">{right}</span>
      </div>
      <div className="h-2 bg-secondary/40 rounded-sm overflow-hidden flex" style={{ width: `${w}%` }}>
        {active != null && passed != null && count > 0 ? (
          <>
            <div className="bg-sky-500/70 h-full" style={{ width: `${(active / count) * 100}%` }} />
            <div className="bg-red-500/40 h-full" style={{ width: `${(passed / count) * 100}%` }} />
          </>
        ) : (
          <div className="bg-sky-500/70 h-full w-full" />
        )}
      </div>
    </div>
  );
}

export default function BrokerStatsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getBrokerStats()
      .then(r => setData(r.data))
      .catch(e => toast.error(e.response?.data?.detail || 'Broker-Statistik konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  if (!data) return <p className="text-sm text-muted-foreground py-10 text-center">Keine Daten verfügbar.</p>;

  const { kpis, monthly, sectors, stages, brokers, checkins } = data;
  const rel = data.relationships || {};
  const chartData = monthly.map(m => ({ ...m, label: monthLabel(m.month) }));
  const maxSector = Math.max(1, ...sectors.map(s => s.total));
  const maxStage = Math.max(1, ...stages.map(s => s.count));

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="broker-stats-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-foreground tracking-tight">Broker-Statistik</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Deal-Flow über Broker: Eingänge, Pipeline-Status, Sektoren, Broker-Ranking und die Wirkung der automatischen Check-ins.
          Absagen zählen als Passed.
        </p>
      </div>

      {/* ── Deal-Flow KPIs ─────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Kpi icon={Inbox} label="Erhalten gesamt" value={kpis.received_total} sub={`${kpis.dismissed} dismissed (kein Deal)`} testId="broker-stats-kpi-received" />
        <Kpi icon={Sparkles} label="Offen in der Inbox" value={kpis.inbox_open} color="text-sky-300" testId="broker-stats-kpi-open" />
        <Kpi icon={CheckCircle2} label="Deals angelegt" value={kpis.deals_created} color="text-emerald-300" testId="broker-stats-kpi-created" />
        <Kpi icon={Layers} label="Aktiv in Pipeline" value={kpis.active} color="text-emerald-300" testId="broker-stats-kpi-active" />
        <Kpi icon={XCircle} label="Passed gesamt" value={kpis.passed_total} sub={`${kpis.declined} abgesagt · ${kpis.passed_deals} später gepasst`} color="text-red-300" testId="broker-stats-kpi-passed" />
        <Kpi icon={FileSignature} label="NDAs gezeichnet" value={kpis.nda_signed} color="text-violet-300" testId="broker-stats-kpi-nda" />
      </div>

      {/* ── Broker relationships ───────────────────────── */}
      <div className="bg-card border border-emerald-500/30 rounded-sm p-4" data-testid="broker-stats-relationships">
        <div className="flex items-center gap-2 mb-3">
          <Handshake className="w-4 h-4 text-emerald-400" />
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Mit wie vielen Brokern sprechen wir</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Kpi icon={Users} label="Broker im CRM" value={rel.crm_total ?? 0} testId="broker-stats-rel-crm" />
          <Kpi icon={Handshake} label="Im Austausch (gesamt)" value={rel.in_contact_total ?? 0} testId="broker-stats-rel-total" />
          <Kpi icon={Activity} label="Aktiv — letzte 30 Tage" value={rel.active_30d ?? 0} color="text-emerald-300" testId="broker-stats-rel-30d" />
          <Kpi icon={Clock} label="Aktiv — letzte 90 Tage" value={rel.active_90d ?? 0} color="text-sky-300" testId="broker-stats-rel-90d" />
          <Kpi icon={Flame} label="Hot Broker" value={rel.hot ?? 0} color="text-orange-300" testId="broker-stats-rel-hot" />
        </div>
        <p className="text-[10px] text-muted-foreground mt-2.5">
          Zählt jeden Broker mit mindestens einem Kontaktpunkt — Deal-Eingang, Call/Meeting, CRM-Gespräch oder Check-in/Touch. „Aktiv" = letzter Kontaktpunkt im Zeitfenster.
        </p>
      </div>

      {/* ── Monthly chart ──────────────────────────────── */}
      <div className="bg-card border border-border rounded-sm p-4" data-testid="broker-stats-monthly-chart">
        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Eingegangene Broker-Deals — letzte 12 Monate</p>
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'var(--muted-foreground, #888)' }} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: 'var(--muted-foreground, #888)' }} axisLine={false} tickLine={false} />
            <RTooltip contentStyle={{ background: '#111', border: '1px solid #333', borderRadius: 4, fontSize: 12 }}
              formatter={(v, n) => [v, n === 'received' ? 'Erhalten' : 'Deals angelegt']} />
            <Bar dataKey="received" name="received" fill="#0ea5e9" fillOpacity={0.65} radius={[2, 2, 0, 0]} maxBarSize={28} />
            <Line dataKey="created" name="created" stroke="#10b981" strokeWidth={2} dot={{ r: 2 }} />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="flex items-center gap-4 mt-2 text-[10px] font-mono text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-sky-500/70 rounded-sm inline-block" /> Erhalten</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-0.5 bg-emerald-500 inline-block" /> Deals angelegt</span>
        </div>
      </div>

      {/* ── Sectors + Stages ───────────────────────────── */}
      <div className="grid md:grid-cols-2 gap-3">
        <div className="bg-card border border-border rounded-sm p-4" data-testid="broker-stats-sectors">
          <div className="flex items-center gap-2 mb-3">
            <Factory className="w-4 h-4 text-sky-400" />
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Sektoren der Broker-Deals</p>
          </div>
          {sectors.length === 0 ? (
            <p className="text-xs text-muted-foreground py-4">Noch keine angelegten Broker-Deals.</p>
          ) : (
            <div className="space-y-3">
              {sectors.map((s, i) => (
                <BarRow key={s.sector} label={s.sector} count={s.total} max={maxSector}
                  active={s.active} passed={s.passed}
                  right={`${s.total} · ${s.active} aktiv · ${s.passed} passed`}
                  testId={`broker-stats-sector-${i}`} />
              ))}
              <p className="text-[10px] text-muted-foreground pt-1">Basis: alle angelegten Brokered Deals (Sektor aus dem Screening). Offene Inbox-Proposals sind noch nicht gescreent.</p>
            </div>
          )}
        </div>
        <div className="bg-card border border-border rounded-sm p-4" data-testid="broker-stats-stages">
          <div className="flex items-center gap-2 mb-3">
            <Layers className="w-4 h-4 text-emerald-400" />
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Wo stehen die Brokered Deals</p>
          </div>
          {stages.length === 0 ? (
            <p className="text-xs text-muted-foreground py-4">Noch keine angelegten Broker-Deals.</p>
          ) : (
            <div className="space-y-3">
              {stages.map((s, i) => (
                <BarRow key={s.slug} label={s.label} count={s.count} max={maxStage}
                  right={`${s.count}`} testId={`broker-stats-stage-${i}`} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Check-in impact ────────────────────────────── */}
      <div className="bg-card border border-violet-500/30 rounded-sm p-4" data-testid="broker-stats-checkins">
        <div className="flex items-center gap-2 mb-3">
          <Flame className="w-4 h-4 text-violet-400" />
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Wirkung der Broker-Check-ins</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi icon={Send} label="Check-ins gesendet" value={checkins.sent_total}
            sub={`${checkins.auto_sent} automatisch · ${checkins.manual_sent} manuell`} testId="broker-stats-checkin-sent" />
          <Kpi icon={Handshake} label="Broker kontaktiert" value={checkins.brokers_contacted} testId="broker-stats-checkin-brokers" />
          <Kpi icon={Reply} label="Antworten danach" value={checkins.brokers_replied}
            sub={`${checkins.reply_rate}% Antwortquote`} color="text-emerald-300" testId="broker-stats-checkin-replies" />
          <Kpi icon={Inbox} label="Neue Deals danach" value={checkins.proposals_after}
            sub={`davon ${checkins.mandates_after} angelegt`} color="text-sky-300" testId="broker-stats-checkin-deals" />
        </div>
        <p className="text-[10px] text-muted-foreground mt-2.5">
          Gezählt werden Antworten und neue Deal-Eingänge von Brokern NACH ihrem ersten Check-in — so siehst du, ob das regelmäßige Nachfassen Mandate bringt.
        </p>
      </div>

      {/* ── Broker ranking ─────────────────────────────── */}
      <CollapsibleSection storageKey="brokerstats.ranking" icon={TrendingUp} iconClass="text-sky-400"
        title="Broker-Ranking — wer bringt die Deals" count={brokers.length} testId="broker-stats-ranking">
        {brokers.length === 0 ? (
          <p className="text-xs text-muted-foreground p-4">Noch keine Broker-Deals erfasst.</p>
        ) : (
          <div className="overflow-x-auto p-2">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="text-left py-2 px-2 font-medium">Broker</th>
                  <th className="text-right py-2 px-2 font-medium">Erhalten</th>
                  <th className="text-right py-2 px-2 font-medium">Angelegt</th>
                  <th className="text-right py-2 px-2 font-medium">Aktiv</th>
                  <th className="text-right py-2 px-2 font-medium" title="Passed Deals + Absagen">Passed</th>
                  <th className="text-right py-2 px-2 font-medium">Offen</th>
                  <th className="text-right py-2 px-2 font-medium" title="Angelegt / bearbeitete Proposals">Conversion</th>
                  <th className="text-right py-2 px-2 font-medium hidden sm:table-cell">Letzter Eingang</th>
                </tr>
              </thead>
              <tbody>
                {brokers.map((b, i) => (
                  <tr key={b.email} className="border-b border-border/40 hover:bg-secondary/30" data-testid={`broker-ranking-row-${i}`}>
                    <td className="py-2 px-2">
                      <span className="text-foreground/90 inline-flex items-center gap-1.5">
                        {b.heat === 'hot' && <Flame className="w-3 h-3 text-orange-400" />}
                        {b.name || b.email}
                      </span>
                      <span className="block text-[10px] text-muted-foreground">{b.company || b.email}</span>
                    </td>
                    <td className="text-right py-2 px-2 font-mono text-foreground/90">{b.received}</td>
                    <td className="text-right py-2 px-2 font-mono text-emerald-300">{b.created}</td>
                    <td className="text-right py-2 px-2 font-mono text-sky-300">{b.active}</td>
                    <td className="text-right py-2 px-2 font-mono text-red-300/90">{b.passed_total}</td>
                    <td className="text-right py-2 px-2 font-mono text-muted-foreground">{b.open}</td>
                    <td className="text-right py-2 px-2 font-mono text-muted-foreground">{b.conversion == null ? '—' : `${b.conversion}%`}</td>
                    <td className="text-right py-2 px-2 font-mono text-muted-foreground hidden sm:table-cell">{fmtDate(b.last_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CollapsibleSection>

      <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
        <Clock className="w-3 h-3" />
        Stand: {new Date(data.generated_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
      </div>
    </div>
  );
}
